"use client";

import { useEffect, useRef, useState } from "react";
import {
  ACTIVE_CATEGORIES, CATEGORIES, IMPRINT_PATH, PRIVACY_PATH, servicesIn, type Category, type OptionalCategory,
} from "@/lib/consent";
import { onOpenSettings, saveConsent, useConsent } from "./store";
import "./consent.css";

type Choice = Record<OptionalCategory, boolean>;
const NONE: Choice = { statistics: false, media: false, marketing: false };
const ALL: Choice = { statistics: true, media: true, marketing: true };
const SHOWN: Category[] = ["necessary", ...ACTIVE_CATEGORIES];

const list = (items: string[]) => (items.length > 1 ? `${items.slice(0, -1).join(", ")} und ${items.at(-1)}` : items[0] ?? "");
const summary = list(ACTIVE_CATEGORIES.map((c) => `${CATEGORIES[c].label} (${servicesIn(c).map((s) => s.name).join(", ")})`));
const thirdCountries = [...new Set(ACTIVE_CATEGORIES.flatMap((c) => servicesIn(c).map((s) => s.thirdCountry).filter(Boolean)))];

const NECESSARY_ITEMS = [
  { name: "Deine Auswahl", detail: "localStorage „gcg-consent“, bis zu 12 Monate, nur in deinem Browser" },
  { name: "Admin-Anmeldung", detail: "Cookie „gcg_session“, nur nach dem Login im Redaktionsbereich" },
];

/** Banner beim ersten Besuch und Dialog für die Einstellungen. Ohne optionale Dienste wird nichts angezeigt. */
export function ConsentManager() {
  const consent = useConsent();
  const dialog = useRef<HTMLDialogElement>(null);
  const [choice, setChoice] = useState<Choice>(NONE);

  const open = () => {
    setChoice(consent && consent !== "unknown" ? { statistics: consent.statistics, media: consent.media, marketing: consent.marketing } : NONE);
    dialog.current?.showModal();
  };
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  });
  useEffect(() => onOpenSettings(() => openRef.current()), []);

  if (!ACTIVE_CATEGORIES.length) return null;

  const decide = (c: Choice, fromBanner: boolean) => {
    saveConsent(c);
    dialog.current?.close();
    if (fromBanner) document.getElementById("inhalt")?.focus({ preventScroll: true });
  };
  const bannerOpen = consent === null;

  return (
    <>
      {bannerOpen && (
        <section className="cc-banner" aria-labelledby="cc-title">
          <h2 id="cc-title">Cookies und externe Inhalte</h2>
          <p>
            Technisch Notwendiges speichern wir immer. Mit deiner Einwilligung laden wir zusätzlich {summary}.
            {thirdCountries.length > 0 && <> Dabei werden Daten an die Anbieter übertragen, auch in die {list(thirdCountries as string[])}.</>}
            {" "}Deine Auswahl kannst du jederzeit über „Cookie-Einstellungen“ unten auf jeder Seite ändern oder widerrufen.
          </p>
          <p className="cc-links"><a href={PRIVACY_PATH}>Datenschutzerklärung</a> · <a href={IMPRINT_PATH}>Impressum</a></p>
          <div className="cc-actions">
            <button type="button" className="cc-btn" onClick={() => decide(ALL, true)}>Alle akzeptieren</button>
            <button type="button" className="cc-btn" onClick={() => decide(NONE, true)}>Nur notwendige</button>
            <button type="button" className="cc-more" onClick={open}>Einstellungen</button>
          </div>
        </section>
      )}

      <dialog ref={dialog} className="cc-dialog" aria-labelledby="cc-dialog-title">
        <div className="cc-dialog-head">
          <h2 id="cc-dialog-title">Cookie-Einstellungen</h2>
          <button type="button" className="cc-close" aria-label="Schließen" onClick={() => dialog.current?.close()}>×</button>
        </div>
        <p>Wähle, was wir laden dürfen. Du kannst die Auswahl jederzeit hier ändern. Mehr in der <a href={PRIVACY_PATH}>Datenschutzerklärung</a>.</p>
        <ul className="cc-cats">
          {SHOWN.map((c) => {
            const fixed = c === "necessary";
            const services: { key: string; name: string; lines: string[]; privacyUrl?: string }[] = fixed
              ? NECESSARY_ITEMS.map((n) => ({ key: n.name, name: n.name, lines: [n.detail] }))
              : servicesIn(c).map((s) => ({
              key: s.id,
              name: s.name,
              lines: [s.purpose, `Anbieter: ${s.provider}`, ...(s.thirdCountry ? [`Übermittlung in Drittland: ${s.thirdCountry}`] : [])],
              privacyUrl: s.privacyUrl,
            }));
            return (
              <li key={c} className="cc-cat">
                <label className="cc-switch">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={fixed || choice[c as OptionalCategory]}
                    disabled={fixed}
                    aria-describedby={`cc-desc-${c}`}
                    onChange={(e) => setChoice((x) => ({ ...x, [c]: e.target.checked }))}
                  />
                  <span className="cc-track" aria-hidden="true" />
                  <span className="cc-label">{CATEGORIES[c].label}{fixed && <span className="cc-always"> (immer aktiv)</span>}</span>
                </label>
                <p id={`cc-desc-${c}`}>{CATEGORIES[c].text}</p>
                <details>
                  <summary>{fixed ? "Details" : `Dienste (${services.length})`}</summary>
                  <dl>
                    {services.map((s) => (
                      <div key={s.key}>
                        <dt>{s.name}</dt>
                        {s.lines.map((l) => <dd key={l}>{l}</dd>)}
                        {s.privacyUrl && <dd><a href={s.privacyUrl} rel="noopener noreferrer" target="_blank">Datenschutzhinweise von {s.name}</a></dd>}
                      </div>
                    ))}
                  </dl>
                </details>
              </li>
            );
          })}
        </ul>
        <div className="cc-actions">
          <button type="button" className="cc-btn" onClick={() => decide(NONE, false)}>Nur notwendige</button>
          <button type="button" className="cc-btn" onClick={() => decide(choice, false)}>Auswahl speichern</button>
          <button type="button" className="cc-btn" onClick={() => decide(ALL, false)}>Alle akzeptieren</button>
        </div>
      </dialog>
    </>
  );
}